import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Package, Eye } from 'lucide-react';
import ImageDownloadMenu from './ImageDownloadMenu';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ withCredentials: true });

export default function BrandKitPanel({ vision, base, q, onUpdate }) {
  const [busy, setBusy] = useState(vision.mockup_status === 'generating');
  const [kitBusy, setKitBusy] = useState(false);
  const pollRef = useRef(null);
  const hasLogo = !!vision.chosen_logo || (vision.assets || []).some((a) => a.kind === 'logo');
  const mockups = vision.mockups || [];
  useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = () => {
    const started = Date.now();
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const r = await ax.get(`${BACKEND}${base}/${vision.vision_id}${q}`).catch(() => null);
      if (!r) return;
      if (r.data.mockup_status !== 'generating' || Date.now() - started > 240000) {
        clearInterval(pollRef.current); setBusy(false); onUpdate(r.data);
        if (r.data.mockup_status === 'ready') toast.success('Your mockups are ready');
        else toast.error(r.data.mockup_error || 'This is taking too long, please try again.');
      }
    }, 5000);
  };
  useEffect(() => { if (vision.mockup_status === 'generating') poll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const makeMockups = async () => {
    setBusy(true);
    try { await ax.post(`${BACKEND}${base}/${vision.vision_id}/mockups${q}`); poll(); }
    catch (e) { setBusy(false); toast.error(e.response?.data?.detail || 'Could not start'); }
  };
  const downloadKit = async () => {
    setKitBusy(true);
    try {
      const r = await ax.get(`${BACKEND}${base}/${vision.vision_id}/brand-kit${q}`, { responseType: 'blob' });
      const name = ((r.headers['content-disposition'] || '').match(/filename="([^"]+)"/) || [])[1] || 'brand-kit.zip';
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('Brand Kit downloaded');
    } catch { toast.error('Could not build the Brand Kit'); }
    finally { setKitBusy(false); }
  };

  if (!hasLogo) return null;
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mt-4" data-testid="brand-kit-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2"><Package size={15} className="text-[#1D4ED8]" /><h5 className="font-bold text-sm text-[#0A0F1C]">Brand kit & mockups</h5></div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={makeMockups} disabled={busy} data-testid="mockups-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#F97316] text-[#C2410C] text-sm font-bold hover:bg-[#FFF7ED] disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} {mockups.length ? 'Redo mockups' : 'See it in real life'}
          </button>
          <button type="button" onClick={downloadKit} disabled={kitBusy} data-testid="brand-kit-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0A0F1C] text-white text-sm font-bold hover:bg-[#1e293b] disabled:opacity-60">
            {kitBusy ? <Loader2 size={14} className="animate-spin" /> : <Package size={14} />} Download Brand Kit
          </button>
        </div>
      </div>
      <p className="text-xs text-[#64748B] mb-3">The Brand Kit includes your logo in PNG, transparent PNG, JPEG and SVG (plus every version), colors with names, HEX and RGB, fonts, brand words, the concept PDF and your mockups.</p>
      {busy && <p className="text-xs text-[#1D4ED8] mb-2 inline-flex items-center gap-1.5" data-testid="mockup-status"><Loader2 size={12} className="animate-spin" /> Placing your logo on a business card, T-shirt, storefront and social profile… (about 30–60 sec)</p>}
      {mockups.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="mockups-grid">
          {mockups.map((m) => {
            const url = `${BACKEND}/api/website-vision/${vision.vision_id}/mockup/${m.mockup_id}`;
            return (
              <div key={m.mockup_id} className="rounded-xl border border-[#E2E8F0] p-2" data-testid={`mockup-${m.kind}`}>
                <a href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt={m.label} className="w-full aspect-square object-cover rounded-lg" /></a>
                <div className="flex items-center justify-between gap-2 mt-1.5">
                  <span className="text-xs font-bold text-[#0A0F1C]">{m.label}</span>
                  <div className="w-24"><ImageDownloadMenu visionId={vision.vision_id} srcId={m.mockup_id} testid={`download-mockup-${m.kind}`} /></div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
